import { forwardRef, useCallback, useMemo } from 'react';
import { alpha, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { jbColors } from 'app/theme/jbColors';
import { useSelector } from 'react-redux';
import { selectIndiaMigrationService } from 'features/entities/entitiesSelectors';
import { selectOSType } from 'features/session/sessionSelectors';
import { ReactComponent as MigrationIndiaIcon } from 'common/assets/jb_india_card.svg';

export const MigrationIndiaServiceCard = forwardRef<HTMLLIElement>((_, ref) => {
  const { t } = useTranslation();
  const osType = useSelector(selectOSType);
  const service = useSelector(selectIndiaMigrationService);

  const labelText = t('migration.india.tile.label');
  const buttonText = t('migration.india.tile.button');
  const titleText = t('migration.india.tile.title');
  const descriptionText = t('migration.india.tile.description');

  const appUrl = useMemo(() => {
    if (osType === 'Android') {
      return service?.androidUrl;
    } else if (osType === 'iOS') {
      return service?.iosUrl;
    } else {
      return service?.url;
    }
  }, [osType, service?.androidUrl, service?.iosUrl, service?.url]);

  const handleDownload = useCallback(() => {
    window.open(appUrl ?? undefined, '_blank');
    return null;
  }, [appUrl]);

  return (
    <Box
      component="li"
      sx={{
        mb: 0.5,
      }}
      ref={ref}
    >
      <Box
        sx={{
          width: 150,
          background: (theme) =>
            `linear-gradient(99.47deg, ${theme.palette.common.white} 0%, #E4E5E6 100.72%)`,
          boxShadow: (theme) =>
            `0px 3px 5px -1px ${alpha(theme.palette.primary.main, 0.1)}, 0px 1px 18px 0px ${alpha(
              theme.palette.common.black,
              0.12
            )}, 0px 6px 10px 0px ${alpha(theme.palette.primary.main, 0.14)}`,
          borderRadius: '5px',
          p: (theme) => theme.spacing(2, 2.5, 3),
          '@media (min-width: 360px)': {
            width: 270,
          },
          '@media (min-width: 481px)': {
            width: 360,
          },
        }}
        onClick={handleDownload}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            '@media (min-width: 481px)': {
              gap: 3,
            },
          }}
        >
          <Box
            sx={{
              '@media (min-width: 360px)': {
                flex: '0 0 150px',
              },
              '@media (min-width: 481px)': {
                flex: '0 0 200px',
              },
            }}
          >
            <Typography
              component="div"
              variant="caption"
              sx={{
                color: 'primary.main',
                fontWeight: 'fontWeightLight',
                textTransform: 'uppercase',
                letterSpacing: '0.08rem',
                lineHeight: 1.6667,
              }}
            >
              {labelText}
            </Typography>
            <Typography
              component="div"
              sx={{
                color: jbColors.support.royalBlue,
                fontWeight: 'fontWeightLight',
                fontSize: (theme) => theme.typography.pxToRem(16),
                lineHeight: 1.37,
                letterSpacing: '0.04rem',
                textTransform: 'uppercase',
                '@media (min-width: 360px)': {
                  fontSize: (theme) => theme.typography.pxToRem(20),
                  lineHeight: 1.3,
                },
              }}
            >
              {titleText}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 114,
              aspectRatio: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '@media (min-width: 360px)': {
                width: 80,
              },
              '@media (min-width: 481px)': {
                width: 114,
              },
            }}
          >
            <MigrationIndiaIcon
              style={{
                width: '100%',
              }}
            />
          </Box>
        </Box>
        <Box>
          <Typography
            variant="body2"
            component="div"
            sx={{ mt: 1, fontWeight: 'fontWeightRegular', lineHeight: 1.42 }}
          >
            {descriptionText}
          </Typography>
        </Box>
        <Box sx={{ pt: 2, display: 'flex' }}>
          <Typography
            variant="body2"
            component="div"
            sx={{
              padding: '2px 5px 2px 5px',
              fontWeight: 'fontWeightBold',
              textAlign: 'center',
              border: `1px solid ${jbColors.support.royalBlue200}`,
              borderRadius: '3px',
              px: 1.5,
              py: 0.75,
            }}
          >
            {buttonText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});
